import type { Components, Theme } from "@mui/material/styles";

import {
  borderWidth,
  color,
  fontWeight,
  lineHeight,
  spacing,
  transition,
} from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

const severities = ["Info", "Success", "Warning", "Error"] as const;

/**
 * Weave tints outlined and standard alerts with
 * `&.MuiAlert-outlined.MuiAlert-colorSuccess { backgroundColor }`, which is
 * three classes of specificity, so a call site's own `background` (the toasts
 * paint themselves white with the `css` prop) can no longer win: the toast
 * showed the 10% tint, and stacked toasts showed through each other. DHIG sets
 * the same tint on the single-class `outlinedSuccess` slots, where call sites
 * still override it. This unsets Weave's rule and puts the tint back on those
 * slots.
 */
const weaveTintKeys = Object.fromEntries(
  ["outlined", "standard"].map((variant) => [
    `&.MuiAlert-${variant}`,
    Object.fromEntries(
      severities.map((severity) => [
        `&.MuiAlert-color${severity}`,
        { backgroundColor: undefined, borderColor: undefined },
      ])
    ),
  ])
);

// Weave writes the filled text colours as `.MuiAlert-filled .MuiAlert-colorInfo`
// (descendant instead of same element), so they never apply and the info and
// error filled alerts kept dark text. Weave's error fill is also a different
// red (`#c34922`) from DHIG's `fill-alert`.
//
// DHIG's `filledInfo` and `filledError` also recolour links, text buttons and
// outlined buttons placed in the alert (including hover, active and
// focus-visible), so those selectors are mirrored here. Filled success and
// warning have no such rules in DHIG.
const buttonStates = [
  "",
  ":hover",
  ":active",
  ":focus:active",
  ":focus-visible",
];
const buttons = ["MuiButton-text", "MuiButton-outlined"];
const outlinedButtons = buttonStates.map(
  (state) => `&&& [class*='MuiButton-outlined']${state}`
);
// DHIG's filled alerts also restyle the button inside the action slot: a
// light (or dark, on success and warning) overlay on hover and press, and a
// two-tone focus ring made of the accent and the fill's ink.
const filledActionButton = (
  hover: string,
  pressed: string,
  accent: string,
  ring: string
) => ({
  "&&& [class*='MuiAlert-action'] > button": {
    backgroundColor: color.fadeTransparent,
    ...transition.blur,
    "&:hover": {
      backgroundColor: hover,
      ...transition.activate,
    },
    "&:active": {
      backgroundColor: pressed,
      ...transition.activate,
    },
    "&:focus-visible": {
      boxShadow: `0px 0px 0px 1px ${accent}, 0px 0px 0px 3px ${ring}`,
    },
  },
});

const onFillActionButton = (accent: string) =>
  filledActionButton(
    color.hoverFillOnFill,
    color.pressedFillOnFill,
    accent,
    color.white
  );

const onLightFillActionButton = (accent: string) =>
  filledActionButton(
    color.hoverFillSubtle,
    color.pressedFillSubtleStrong,
    accent,
    color.black
  );

const filledInk = (token: string) => ({
  [[
    "& [class*='MuiAlert-icon']",
    "& [class*='MuiAlert-message']",
    "& [class*='MuiAlert-action'] > button > svg",
    "& [class*='MuiLink-root']",
    ...[":hover", ":active", ":focus-visible"].map(
      (state) => `&& [class*='MuiLink-root']${state}`
    ),
    ...buttons.flatMap((cls) =>
      buttonStates.map((state) => `&&& [class*='${cls}']${state}`)
    ),
  ].join(", ")]: { color: token },
  [outlinedButtons.join(", ")]: { borderColor: token },
  "& [class*='MuiLink-root']": { cursor: "pointer" },
  // DHIG uses the engage ink for the link underline and the focus ring in the
  // error alert as well.
  "& [class*='MuiLink-root'], && [class*='MuiLink-root']:focus-visible": {
    textDecorationColor: color.inkOnFill,
  },
  [[
    "&& [class*='MuiLink-root']:focus-visible",
    "&&& [class*='MuiButton-text']:focus-visible",
    "&&& [class*='MuiButton-outlined']:focus-visible",
  ].join(", ")]: { outlineColor: color.inkOnFill },
  "&& [class*='MuiLink-root']:hover": { textDecoration: "none" },
});

const tints = {
  Info: color.irisSlight,
  Success: color.successSlight,
  Warning: color.warningSlight,
  Error: color.alertSlight,
};

const borders = {
  Info: color.iris,
  Success: color.success,
  Warning: color.warning,
  Error: color.alert,
};

// Weave's outlined `borderColor` has the same three-class specificity, so the
// toasts' grey `border` was lost too; it is unset above and restored here.
const tintSlots = Object.fromEntries(
  ["outlined", "standard"].flatMap((variant) =>
    severities.map((severity) => [
      `${variant}${severity}`,
      {
        backgroundColor: tints[severity],
        ...(variant === "outlined" && {
          border: `${borderWidth.thin} solid ${borders[severity]}`,
        }),
      },
    ])
  )
);

/**
 * Restores the Digital HIG Alert. Besides the background (see above), Weave's
 * `.MuiAlert-message` leaves MUI's 8px padding in place and drops DHIG's
 * compact line height, and the root loses DHIG's body typography.
 */
export const alertOverrides: ThemeComponents = {
  MuiAlert: {
    // DHIG's defaults; Weave sets none, so a bare `<Alert>` had no border
    // (MUI "standard") and rendered green (MUI "success").
    defaultProps: { variant: "outlined", severity: "info" },
    styleOverrides: {
      root: {
        ...typographyVariant["short-copy-small"],
        fontWeight: fontWeight.medium,
        color: color.ink,
        ...weaveTintKeys,
        "&.MuiAlert-filled": {
          "&.MuiAlert-colorError": { backgroundColor: undefined },
        },
        // Weave stretches the message to the full width (`width: 100%`), so a
        // toast filled its wrapper instead of hugging its text as under DHIG.
        ".MuiAlert-message": { width: undefined },
        // Weave's own `action` slot (padding, margins) is what DHIG wants, but
        // its root-level `.MuiAlert-action { padding: 0; marginRight: 0 }`
        // comes later in the cascade and cancels it.
        ".MuiAlert-action": {
          marginRight: undefined,
          padding: undefined,
          // DHIG leaves the action wrapper alone; only its button is a target.
          cursor: undefined,
        },
        '& [class*="MuiAlert-action"] > button': {
          backgroundColor: color.fadeTransparent,
        },
        "& .MuiAlert-message": {
          lineHeight: lineHeight.compact,
          margin: "10px 0",
          padding: spacing.none,
          paddingTop: "1px",
          overflow: "hidden",
        },
      },
      // Weave pads the close button 8px from the message, DHIG 4px.
      action: { "&&": { paddingLeft: spacing.xxs } },
      // Weave pads every variant 1px; DHIG pads the standard and filled ones
      // 2px (outlined has a 1px border and keeps 1px).
      standard: { paddingTop: "2px", paddingBottom: "2px" },
      filled: { paddingTop: "2px", paddingBottom: "2px" },
      filledInfo: {
        ...filledInk(color.inkOnFill),
        ...onFillActionButton(color.iris),
      },
      filledError: {
        backgroundColor: color.alert,
        ...filledInk(color.inkOnFill),
        ...onFillActionButton(color.errorField),
      },
      filledSuccess: onLightFillActionButton(color.success),
      filledWarning: onLightFillActionButton(color.warning),
      ...tintSlots,
    },
  },
};
